import React, { useState, useEffect } from 'react';
import './MaterialOrdersListModal.css';

const MaterialOrdersListModal = ({ open, onClose, onOrdersChange }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Fetch orders when modal opens
  useEffect(() => {
    if (open) {
      fetchOrders();
    }
  }, [open]);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/material-orders`);
      if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.statusText}`);
      }
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/material-orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update order status');
      }

      // If status is changed to received, create raw materials
      if (newStatus === 'received') {
        await markOrderAsReceived(orderId);
      }

      fetchOrders(); // Refresh the list
      if (onOrdersChange) {
        onOrdersChange(); // Notify parent to refresh materials list
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating order status:', err);
    }
  };

  const markOrderAsReceived = async (orderId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/material-orders/${orderId}/receive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process order receipt');
      }

      console.log('Order marked as received and raw materials created');
    } catch (err) {
      setError(err.message);
      console.error('Error processing order receipt:', err);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/material-orders/${orderId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete order');
      }

      fetchOrders(); // Refresh the list
      if (onOrdersChange) {
        onOrdersChange();
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting order:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'ordered': return '#2196f3';
      case 'received': return '#4caf50';
      case 'cancelled': return '#f44336';
      default: return '#757575';
    }
  };

  const getStatusActions = (order) => {
    switch (order.status) {
      case 'pending':
        return (
          <div className="status-actions">
            <button 
              className="btn-status btn-ordered"
              onClick={() => updateOrderStatus(order.id, 'ordered')}
            >
              Mark as Ordered
            </button>
            <button 
              className="btn-status btn-cancel"
              onClick={() => updateOrderStatus(order.id, 'cancelled')}
            >
              Cancel
            </button>
          </div>
        );
      case 'ordered':
        return (
          <div className="status-actions">
            <button 
              className="btn-status btn-received"
              onClick={() => updateOrderStatus(order.id, 'received')}
            >
              Mark as Received
            </button>
            <button 
              className="btn-status btn-cancel"
              onClick={() => updateOrderStatus(order.id, 'cancelled')}
            >
              Cancel
            </button>
          </div>
        );
      case 'received':
        return (
          <div className="status-actions">
            <span className="status-complete">✅ Complete</span>
          </div>
        );
      case 'cancelled':
        return (
          <div className="status-actions">
            <span className="status-cancelled">❌ Cancelled</span>
          </div>
        );
      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content material-orders-list-modal">
        <div className="modal-header">
          <h2>📦 Material Orders</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {/* Action Bar */}
          <div className="action-bar">
            <div className="orders-count">
              {orders.length} order{orders.length !== 1 ? 's' : ''} found
            </div>
            <button 
              className="btn-refresh"
              onClick={fetchOrders}
              disabled={loading}
            >
              🔄 Refresh
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading orders...</p>
            </div>
          ) : (
            /* Orders List */
            <div className="orders-list">
              {orders.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <h3>No Orders Found</h3>
                  <p>No material orders have been created yet.</p>
                </div>
              ) : (
                orders.map(order => (
                  <div key={order.id} className="order-card">
                    <div className="order-header">
                      <div className="order-info">
                        <h3>Order #{order.orderNumber}</h3>
                        <div className="order-meta">
                          <span className="supplier-name">📍 {order.supplier.name}</span>
                          <span 
                            className="order-status"
                            style={{ backgroundColor: getStatusColor(order.status) }}
                          >
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="order-value">
                        <span className="value-label">Total Value</span>
                        <span className="value-amount">R{parseFloat(order.totalValue || 0).toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="order-details">
                      <div className="detail-row">
                        <span className="detail-label">Work Ticket:</span>
                        <span className="detail-value">{order.workTicketNr || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Expected Date:</span>
                        <span className="detail-value">
                          {order.expectedDate ? new Date(order.expectedDate).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Created:</span>
                        <span className="detail-value">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {order.notes && (
                        <div className="detail-row">
                          <span className="detail-label">Notes:</span>
                          <span className="detail-value">{order.notes}</span>
                        </div>
                      )}
                    </div>

                    <div className="order-items">
                      <h4>Order Items ({order.orderItems.length})</h4>
                      <div className="items-list">
                        {order.orderItems.map((item, index) => (
                          <div key={index} className="order-item">
                            <span className="item-material">{item.materialType.productCode}</span>
                            <span className="item-specs">{item.width}mm × {item.length}m</span>
                            <span className="item-quantity">Qty: {item.quantity}</span>
                            <span className="item-price">R{parseFloat(item.totalPrice || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="order-actions">
                      {getStatusActions(order)}
                      <div className="action-buttons">
                        <button
                          className="btn-delete-small"
                          onClick={() => deleteOrder(order.id)}
                          title="Delete order"
                          disabled={order.status === 'received'}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialOrdersListModal;
